var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, Box, Card, CardContent, CardHeader, Chip, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography, } from "@mui/material";
import { useMemo, useState } from "react";
import { useListAggregatedPlayerRecordsByTeam, useListAggregatedPlayerStatsByTeam, } from "../api/StatsApiProvider";
import { useRetrieveTeam } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { PlayerNameLink } from "../players/PlayerDisplayNameLink";
import { useNumericParams } from "../utils/useNumericParams";
import { aggregatePlayerHonors, aggregatePlayerRecords, aggregatePlayerStats, } from "./utils";
var LABELS = {
    intra_squad_match: "Intra-Squad Matches",
    intra_squad_tournament_match: "Intra-Squad Tournaments",
    external_match: "External Matches",
};
var PLAYER_VIEW_HEAD_CELLS = [
    { id: "player", label: "Player" },
    { id: "matchesPlayed", label: "Matches Played" },
    { id: "goalsScored", label: "Goals Scored" },
    { id: "yellowCards", label: "Yellows" },
    { id: "redCards", label: "Reds" },
];
var PLAYER_RECORDS_HEAD_CELLS = [
    { id: "player", label: "Player" },
    { id: "wins", label: "Wins" },
    { id: "draws", label: "Draws" },
    { id: "losses", label: "Losses" },
    { id: "winPercentage", label: "Win %" },
];
var PLAYER_HONORS_HEAD_CELLS = [
    { id: "player", label: "Player" },
    { id: "participated", label: "Intra-Squad Tournaments Participated" },
    { id: "first", label: "Winner" },
    { id: "second", label: "Runner-Up" },
    { id: "third", label: "Third-Place" },
];
export var StatsDetails = function () {
    var teamId = useNumericParams().id;
    var _a = useRetrieveTeam({ id: teamId }), team = _a.data, teamIsLoading = _a.isLoading, teamError = _a.error;
    var _b = useListAggregatedPlayerStatsByTeam({ teamId: teamId }), aggregatedStats = _b.data, aggregatedStatsIsLoading = _b.isLoading, aggregatedStatsError = _b.error;
    var _c = useListAggregatedPlayerRecordsByTeam({ teamId: teamId }), aggregatedPlayerRecords = _c.data, aggregatedPlayerRecordsIsLoading = _c.isLoading, aggregatedPlayerRecordsError = _c.error;
    var allYears = useMemo(function () {
        return Array.from(new Set(__spreadArray(__spreadArray([], __read(((aggregatedStats === null || aggregatedStats === void 0 ? void 0 : aggregatedStats.flatMap(function (s) { return s.stats.map(function (r) { return r.year; }); })) ||
            [])), false), __read(((aggregatedPlayerRecords === null || aggregatedPlayerRecords === void 0 ? void 0 : aggregatedPlayerRecords.flatMap(function (r) {
            return r.matchRecords.map(function (m) { return m.year; });
        })) || [])), false))).sort();
    }, [aggregatedStats, aggregatedPlayerRecords]);
    var _d = __read(useState([]), 2), yearFilter = _d[0], setYearFilter = _d[1];
    var _e = __read(useState([]), 2), typeFilter = _e[0], setTypeFilter = _e[1];
    var _f = __read(useState("asc"), 2), order = _f[0], setOrder = _f[1];
    var _g = __read(useState("player"), 2), orderBy = _g[0], setOrderBy = _g[1];
    var _h = __read(useState("asc"), 2), orderRecords = _h[0], setOrderRecords = _h[1];
    var _j = __read(useState("player"), 2), orderByRecords = _j[0], setOrderByRecords = _j[1];
    var _k = __read(useState("asc"), 2), orderHonors = _k[0], setOrderHonors = _k[1];
    var _l = __read(useState("player"), 2), orderByHonors = _l[0], setOrderByHonors = _l[1];
    var isLoading = aggregatedStatsIsLoading ||
        teamIsLoading ||
        aggregatedPlayerRecordsIsLoading;
    var sortedStats = useMemo(function () {
        var playerStats = aggregatePlayerStats(aggregatedStats, {
            years: yearFilter,
            types: typeFilter,
        });
        return __spreadArray([], __read(playerStats), false).sort(function (a, b) {
            if (orderBy === "player") {
                return order === "asc"
                    ? a.player.name.localeCompare(b.player.name)
                    : b.player.name.localeCompare(a.player.name);
            }
            return order === "asc"
                ? a[orderBy] -
                    b[orderBy]
                : b[orderBy] -
                    a[orderBy];
        });
    }, [aggregatedStats, order, orderBy, yearFilter, typeFilter]);
    var sortedRecords = useMemo(function () {
        var playerRecords = aggregatePlayerRecords(aggregatedPlayerRecords, {
            years: yearFilter,
            types: typeFilter,
        });
        return __spreadArray([], __read(playerRecords), false).sort(function (a, b) {
            if (orderByRecords === "player") {
                return orderRecords === "asc"
                    ? a.player.name.localeCompare(b.player.name)
                    : b.player.name.localeCompare(a.player.name);
            }
            return orderRecords === "asc"
                ? a[orderByRecords] -
                    b[orderByRecords]
                : b[orderByRecords] -
                    a[orderByRecords];
        });
    }, [
        aggregatedPlayerRecords,
        orderRecords,
        orderByRecords,
        yearFilter,
        typeFilter,
    ]);
    var sortedHonors = useMemo(function () {
        var playerHonors = aggregatePlayerHonors(aggregatedPlayerRecords, {
            years: yearFilter,
            types: typeFilter,
        });
        return __spreadArray([], __read(playerHonors), false).sort(function (a, b) {
            if (orderByHonors === "player") {
                return orderHonors === "asc"
                    ? a.player.name.localeCompare(b.player.name)
                    : b.player.name.localeCompare(a.player.name);
            }
            return orderHonors === "asc"
                ? a[orderByHonors] -
                    b[orderByHonors]
                : b[orderByHonors] -
                    a[orderByHonors];
        });
    }, [
        aggregatedPlayerRecords,
        orderHonors,
        orderByHonors,
        yearFilter,
        typeFilter,
    ]);
    if (isLoading)
        return _jsx(Loading, {});
    if (aggregatedStatsError || teamError || aggregatedPlayerRecordsError) {
        return (_jsx(ErrorView, { error: aggregatedStatsError || teamError || aggregatedPlayerRecordsError }));
    }
    if (!team) {
        return _jsx(ErrorView, { error: new Error("Team data not found") });
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Typography, { variant: "h5", mb: 2, children: ["Player Statistics for ", (team === null || team === void 0 ? void 0 : team.name) || "Team"] }), _jsxs(Box, { display: "flex", gap: 2, mb: 2, children: [_jsx(Autocomplete, { multiple: true, options: allYears, value: yearFilter, onChange: function (_, value) { return setYearFilter(value); }, disableCloseOnSelect: true, renderTags: function (value, getTagProps) {
                            return value.map(function (option, index) { return (_jsx(Chip, __assign({ label: option }, getTagProps({ index: index })))); });
                        }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: "Year Filter (All Cards)" }))); }, sx: { minWidth: 300 } }), _jsx(Autocomplete, { multiple: true, options: Object.keys(LABELS), getOptionLabel: function (option) { return LABELS[option] || option; }, value: typeFilter, onChange: function (_, value) {
                            return setTypeFilter(value);
                        }, disableCloseOnSelect: true, renderTags: function (value, getTagProps) {
                            return value.map(function (option, index) { return (_jsx(Chip, __assign({ label: LABELS[option] || option }, getTagProps({ index: index })))); });
                        }, renderInput: function (params) { return _jsx(TextField, __assign({}, params, { label: "Match Type" })); }, sx: { minWidth: 300 } })] }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsxs(Card, { children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "h6", children: "Performance Metrics: Goals & Discipline" }) }), _jsx(CardContent, { children: _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsx(TableRow, { children: PLAYER_VIEW_HEAD_CELLS.map(function (headCell) { return (_jsx(TableCell, { children: _jsx(TableSortLabel, { active: orderBy === headCell.id, direction: orderBy === headCell.id ? order : "asc", onClick: function () {
                                                                    var isAsc = orderBy === headCell.id && order === "asc";
                                                                    setOrder(isAsc ? "desc" : "asc");
                                                                    setOrderBy(headCell.id);
                                                                }, children: headCell.label }) }, headCell.id)); }) }) }), _jsx(TableBody, { children: sortedStats.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 5, align: "center", children: "No stats available yet!" }) })) : (sortedStats.map(function (stat, index) { return (_jsxs(TableRow, { sx: {
                                                            backgroundColor: index % 2 ? "white" : "whitesmoke",
                                                        }, children: [_jsx(TableCell, { children: _jsx(PlayerNameLink, { id: stat.player.id, name: stat.player.name, nationality: stat.player.nationality }) }), _jsx(TableCell, { children: stat.matchesPlayed }), _jsx(TableCell, { children: stat.goalsScored }), _jsx(TableCell, { children: stat.yellowCards }), _jsx(TableCell, { children: stat.redCards })] }, stat.player.id)); })) })] }) }) })] }) }), _jsx(Grid, { item: true, xs: 12, md: 6, children: _jsxs(Card, { children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "h6", children: "Records: Wins, Losses, Draws" }) }), _jsx(CardContent, { children: _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsx(TableRow, { children: PLAYER_RECORDS_HEAD_CELLS.map(function (headCell) { return (_jsx(TableCell, { children: _jsx(TableSortLabel, { active: orderByRecords === headCell.id, direction: orderByRecords === headCell.id
                                                                    ? orderRecords
                                                                    : "asc", onClick: function () {
                                                                    var isAsc = orderByRecords === headCell.id &&
                                                                        orderRecords === "asc";
                                                                    setOrderRecords(isAsc ? "desc" : "asc");
                                                                    setOrderByRecords(headCell.id);
                                                                }, children: headCell.label }) }, headCell.id)); }) }) }), _jsx(TableBody, { children: sortedRecords.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 5, align: "center", children: "No stats available yet!" }) })) : (sortedRecords.map(function (stat, index) { return (_jsxs(TableRow, { sx: {
                                                            backgroundColor: index % 2 ? "white" : "whitesmoke",
                                                        }, children: [_jsx(TableCell, { children: _jsx(PlayerNameLink, { id: stat.player.id, name: stat.player.name, nationality: stat.player.nationality }) }), _jsx(TableCell, { children: stat.wins }), _jsx(TableCell, { children: stat.draws }), _jsx(TableCell, { children: stat.losses }), _jsx(TableCell, { children: stat.winPercentage !== null
                                                                    ? "".concat((stat.winPercentage * 100).toFixed(1), "%")
                                                                    : "—" })] }, stat.player.id)); })) })] }) }) })] }) }), _jsx(Grid, { item: true, xs: 12, md: 6, children: _jsxs(Card, { children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "h6", children: "Honors" }) }), _jsx(CardContent, { children: _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsx(TableRow, { children: PLAYER_HONORS_HEAD_CELLS.map(function (headCell) { return (_jsx(TableCell, { children: _jsx(TableSortLabel, { active: orderByHonors === headCell.id, direction: orderByHonors === headCell.id
                                                                    ? orderHonors
                                                                    : "asc", onClick: function () {
                                                                    var isAsc = orderByHonors === headCell.id &&
                                                                        orderHonors === "asc";
                                                                    setOrderHonors(isAsc ? "desc" : "asc");
                                                                    setOrderByHonors(headCell.id);
                                                                }, children: headCell.label }) }, headCell.id)); }) }) }), _jsx(TableBody, { children: sortedHonors.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 5, align: "center", children: "No stats available yet!" }) })) : (sortedHonors.map(function (stat, index) { return (_jsxs(TableRow, { sx: {
                                                            backgroundColor: index % 2 ? "white" : "whitesmoke",
                                                        }, children: [_jsx(TableCell, { children: _jsx(PlayerNameLink, { id: stat.player.id, name: stat.player.name, nationality: stat.player.nationality }) }), _jsx(TableCell, { children: stat.participated }), _jsx(TableCell, { children: stat.first }), _jsx(TableCell, { children: stat.second }), _jsx(TableCell, { children: stat.third })] }, stat.player.id)); })) })] }) }) })] }) })] })] }));
};
